/* eslint-disable no-undef */
import React, { useEffect, useRef, useState, CSSProperties } from "react";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  List,
  Popconfirm,
  Popover,
  Row,
  Select,
  Spin,
} from "antd";
import { useStore } from "../../stores/root-store-context";
import { CloseOutlined } from "@ant-design/icons";
import { getPhoneFormat } from "../helpers/phoneFormat";
import { validateZipCode } from "../helpers/zipValidator";
import type { MemberInfo } from "../../api/ApplicationApi";
import "./MemberRow.scss";

const attachDateMask = (
  el: HTMLElement | null,
  onComplete: (date: dayjs.Dayjs) => void
): void => {
  if (!el) return;
  const input = el.querySelector("input");
  if (!input || (input as any).__maskAttached) return;
  (input as any).__maskAttached = true;
  input.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Backspace" || e.key === "Delete" || e.key === "Tab" || e.key === "Enter") return;
    if (!/^\d$/.test(e.key)) return;
    e.preventDefault();
    const digits = (input.value.replace(/\D/g, "") + e.key).slice(0, 8);
    let masked = digits;
    if (digits.length > 4) masked = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    else if (digits.length > 2) masked = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    input.value = masked;
    if (digits.length === 8) {
      const parsed = dayjs(`${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`, "MM/DD/YYYY");
      if (parsed.isValid()) onComplete(parsed);
    }
  });
};

type MemberRowProps = {
  MemberInfo: MemberInfo;
  background: string;
  changeForm: (key: string, changedFields: any[], allFields: any[]) => void;
  saveForm: (key: string) => void;
  removeForm: (key: string) => void;
  onPressEnterForm: (key: string) => void;
  style?: CSSProperties;
};

export const MemberRow = observer((props: MemberRowProps) => {
  const { applicationStore, membersStore } = useStore();
  const [form] = Form.useForm();
  const beginTermRef = useRef<HTMLDivElement>(null);
  const endTermRef = useRef<HTMLDivElement>(null);

  const [notProvider, setNotProvider] = useState(false);
  const [noAccount, setNoAccount] = useState(false);
  const [isConfirmedEmailVisible, setIsConfirmedEmailVisible] = useState(false);
  const [isEditForm, setIsEditForm] = useState(false);
  const [isOpenOver, setIsOpenOver] = useState(false);
  const [isValidAccountNumber, setIsValidAccountNumber] = useState(
    props.MemberInfo.residentKey > 0 && props.MemberInfo.residentContactKey !== ""
  );

  let timer: ReturnType<typeof setTimeout> | null = null;

  const isFinCen = applicationStore.isFinCen === true;

  const checkNoAccount = (member: any): boolean => {
    if (member.committeeMemberKey == 0 || member.committeeMemberKey == "") return false;
    if (member.residentContactKey && member.residentContactKey !== "") {
      return false;
    }
    return true;
  };

  const runValidFiled = (name: string): void => {
    const data = form.getFieldValue(name);
    form.setFieldsValue({ [name]: name });
    form.setFieldsValue({ [name]: data });
  };

  const getDataStr = (date: any): string => {
    if (date)
      return `${date.year()}-${date.month() < 9 ? `0${date.month() + 1}` : date.month() + 1}-${date.date()}`;
    return "";
  };

  useEffect(() => {
    props.MemberInfo.positions = applicationStore.positions
      .filter(
        (pos) =>
          pos.groupDisplay &&
          pos.groupDisplay.trim().toLocaleUpperCase() ===
            applicationStore.comiteeName.trim().toLocaleUpperCase()
      )
      .map((position) => position.positionName);

    if (props.MemberInfo.cm_Title !== "") {
      const isExist = props.MemberInfo.positions.find(
        (pos) =>
          pos.trim().toLocaleUpperCase() === props.MemberInfo.cm_Title.trim().toLocaleUpperCase()
      );
      if (!isExist) props.MemberInfo.positions = [...props.MemberInfo.positions, props.MemberInfo.cm_Title];
    }
    if (props.MemberInfo.positions?.length === 1 && props.MemberInfo.cm_Title === "") {
      props.MemberInfo.cm_Title = props.MemberInfo.positions[0];
    }

    props.MemberInfo.noAccount = checkNoAccount(props.MemberInfo);
    props.MemberInfo.committeeName = props.MemberInfo.cm_Title;
    props.MemberInfo.groupName = props.MemberInfo.cm_Title;
    props.MemberInfo.propertyName = applicationStore.propertyName;
    if (props.MemberInfo.committeeKey === 0 && applicationStore.comiteeId) {
      props.MemberInfo.committeeKey = parseInt(applicationStore.comiteeId);
    }
    setIsConfirmedEmailVisible(form.getFieldValue("cmc_Email") != "");
    setNotProvider(form.getFieldValue("cmc_Email") == "");
    setNoAccount(checkNoAccount(props.MemberInfo));
    form.setFieldsValue({ ...props.MemberInfo });
  }, []);

  useEffect(() => {
    props.MemberInfo.committeeName = props.MemberInfo.cm_Title;
    props.MemberInfo.groupName = props.MemberInfo.cm_Title;
  }, [props.MemberInfo.cm_Title]);

  useEffect(() => {
    if (isFinCen) {
      form.setFieldsValue({ isEmailConfirmed: true });
    }
  }, [isFinCen]);

  useEffect(() => {
    attachDateMask(beginTermRef.current, (parsed) => {
      form.setFieldsValue({ termStartStrPicker: parsed, termStartStr: getDataStr(parsed) });
      setIsEditForm(true);
    });
    attachDateMask(endTermRef.current, (parsed) => {
      form.setFieldsValue({ termEndStrPicker: parsed, termEndStr: getDataStr(parsed) });
      setIsEditForm(true);
    });
  }, []);

  return (
    <Card
      data-testid={`mat-row-member-${props.MemberInfo.key}`}
      style={{
        background: props.background,
        borderRadius: 12,
        width: "100%",
        height: "100%",
        boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
        marginBottom: 18,
        padding: 16,
        ...props.style,
      }}
      className="member-row"
      bodyStyle={{ padding: 0 }}
    >
      <Form
        form={form}
        variant="outlined"
        layout="vertical"
        initialValues={props.MemberInfo}
        labelCol={{ span: 24 }}
        validateMessages={{}}
        autoComplete="off"
        onFieldsChange={(changedFields, allFields) => {
          setIsConfirmedEmailVisible(form.getFieldValue("cmc_Email") != "");
          if (timer) clearTimeout(timer);
          timer = setTimeout(() => {
            setIsEditForm(true);
            props.changeForm(props.MemberInfo.key, changedFields, allFields);
          }, 100);
        }}
        onFinish={() => {
          setTimeout(() => {
            setIsEditForm(false);
            props.saveForm(props.MemberInfo.key);
          }, 100);
        }}
        scrollToFirstError={false}
      >
        <Row>
          <Col span={22}>

            <div className="member-row__grid">
              <Form.Item<MemberInfo> name="key" hidden>
                <Input />
              </Form.Item>
              <Form.Item<MemberInfo>
                name="cm_Title"
                rules={[{ required: !isFinCen, message: "Please select Position." }]}
                label="Position"
                className="member-row__field--wide"
              >
                <Select
                  data-testid={`mat-select-position-${props.MemberInfo.key}`}
                  className="select-short"
                  disabled={isFinCen}
                  placeholder="Position"
                  options={props.MemberInfo.positions?.map((pos) => ({ value: pos, label: pos }))}
                  onChange={(e) => {
                    form.setFieldsValue({ committeeName: e, groupName: e });
                  }}
                />
              </Form.Item>
              <div className="member-row__name-group">
                <Form.Item<MemberInfo>
                  name="lastName"
                  rules={[{ required: !isFinCen, message: "Please enter Last Name.", min: 1 }]}
                  label="Last Name"
                >
                  <Input data-testid={`mat-input-last-name-${props.MemberInfo.key}`} disabled={isFinCen} placeholder="Last Name" />
                </Form.Item>
                <Form.Item<MemberInfo>
                  name="firstName"
                  rules={[{ required: !isFinCen, message: "Please enter First Name.", min: 1 }]}
                  label="First Name"
                >
                  <Input data-testid={`mat-input-first-name-${props.MemberInfo.key}`} disabled={isFinCen} placeholder="First Name" />
                </Form.Item>
              </div>
              <Form.Item<MemberInfo>
                name="streetName"
                rules={[{ required: !isFinCen, message: "Please enter Street Address.", min: 1 }]}
                label="Street Address"
                className="member-row__field--wide"
              >
                <Input data-testid={`mat-input-street-${props.MemberInfo.key}`} disabled={isFinCen} placeholder="Street Address" />
              </Form.Item>
              <Form.Item<MemberInfo>
                name="city"
                rules={[{ required: !isFinCen, message: "Please enter City.", min: 1 }]}
                label="City"
              >
                <Input data-testid={`mat-input-city-${props.MemberInfo.key}`} disabled={isFinCen} placeholder="City" />
              </Form.Item>
              <Form.Item<MemberInfo>
                name="state"
                label="State"
                className="member-row__field--narrow"
                rules={[
                  { required: !isFinCen },
                  {
                    validator(_, value: string) {
                      if (isFinCen || !value) return Promise.resolve();
                      if (value.length === 2 && /^[a-zA-Z]{2}$/.test(value)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Please enter a valid state code. State codes should be entered as a two-letter abbreviation.")
                      );
                    },
                  },
                ]}
              >
                <Input data-testid={`mat-input-state-${props.MemberInfo.key}`} disabled={isFinCen} placeholder="State" />
              </Form.Item>
              <Form.Item<MemberInfo>
                name="zip"
                label="ZIP"
                className="member-row__field--narrow"
                rules={[
                  { required: !isFinCen },
                  {
                    validator(_, value: string) {
                      if (isFinCen || !value) return Promise.resolve();
                      if (validateZipCode(value)) return Promise.resolve();
                      return Promise.reject(new Error("Please enter a valid ZIP."));
                    },
                  },
                ]}
              >
                <Input data-testid={`mat-input-zip-${props.MemberInfo.key}`} disabled={isFinCen} placeholder="ZIP" />
              </Form.Item>
            </div>
            
            <div className="member-row__grid">
              <div className="member-row__field--wide">
                <Popover
                  content={
                    <Spin
                      spinning={
                        !membersStore.memberInfoByAccount.find((mi) => mi.key === props.MemberInfo.key)
                      }
                    >
                      <List size="small">
                        {membersStore.memberInfoByAccount.find((mi) => mi.key === props.MemberInfo.key)?.members.length === 0 ? (
                          <List.Item>Invalid account</List.Item>
                        ) : (
                          membersStore.memberInfoByAccount
                            .find((mi) => mi.key === props.MemberInfo.key)
                            ?.members.map((m, idx) => (
                              <List.Item
                                key={m.committeeKey + idx}
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  form.setFieldsValue({ ...m });
                                  setNotProvider(m.cmc_Email == "");
                                  setIsConfirmedEmailVisible(m.cmc_Email != "");
                                  setIsValidAccountNumber(true);
                                  setIsOpenOver(false);
                                }}
                              >
                                {`${m.firstName} ${m.lastName}`}
                              </List.Item>
                            ))
                        )}
                      </List>
                    </Spin>
                  }
                  trigger="click"
                  open={isOpenOver}
                  placement="right"
                  onOpenChange={() => setIsOpenOver(false)}
                >
                  <Form.Item<MemberInfo>
                    name="residentKey"
                    label="Account"
                    style={{ marginBottom: 0 }}
                    rules={[
                      {
                        validator() {
                          if (isFinCen) return Promise.resolve();
                          if ((isValidAccountNumber && !noAccount) || noAccount) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error("Please enter Account number and select resident.")
                          );
                        },
                      },
                      {
                        transform: (e: string) => {
                          form.setFieldsValue({ residentKey: e.replace(/\D/g, "") });
                        },
                      },
                    ]}
                  >
                    <Input
                      data-testid={`mat-input-account-${props.MemberInfo.key}`}
                      placeholder="Account #"
                      disabled={noAccount || props.MemberInfo.isDisabledAccount || isFinCen}
                      onChange={() => setIsValidAccountNumber(true)}
                      onPressEnter={e => {
                        e.preventDefault();
                        props.onPressEnterForm(props.MemberInfo.key);
                        setIsOpenOver(true);
                      }}
                    />
                  </Form.Item>
                </Popover>
                <Checkbox
                  data-testid={`mat-checkbox-no-account-${props.MemberInfo.key}`}
                  style={{ marginTop: 8 }}
                  onChange={e => {
                    form.setFieldsValue({ noAccount: e.target.checked });
                    setNoAccount(e.target.checked);
                    if (e.target.checked) {
                      form.setFieldsValue({ residentKey: "" });
                      runValidFiled("residentKey");
                    }
                  }}
                  checked={noAccount}
                  disabled={props.MemberInfo.isDisabledAccount || isFinCen}
                >
                  <b>No Account #</b>
                </Checkbox>
              </div>
              <div className="member-row__field--wide">
                <Form.Item<MemberInfo> name="committeeMemberContactKey" hidden>
                  <Input />
                </Form.Item>
                <Form.Item<MemberInfo> name="residentContactKey" hidden>
                  <Input />
                </Form.Item>
                <Form.Item<MemberInfo>
                  name="cmc_Email"
                  label="Email"
                  style={{ marginBottom: 0 }}
                  rules={[
                    {
                      validator(_, value: string) {
                        if (value || notProvider) {
                          if (notProvider || form.getFieldValue("isEmailConfirmed")) return Promise.resolve();
                          if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) return Promise.resolve();
                          return Promise.reject(new Error("Please enter valid email!"));
                        }
                        return Promise.reject(new Error("Please enter your email!"));
                      },
                    },
                    {
                      transform: (e: string) => {
                        form.setFieldsValue({ cmc_Email: e.trim() });
                      },
                    },
                  ]}
                >
                  <Input data-testid={`mat-input-email-${props.MemberInfo.key}`} placeholder="Email Address" disabled={notProvider || isFinCen} />
                </Form.Item>
                {!isConfirmedEmailVisible ? (
                  <Checkbox
                    style={{ marginTop: 8 }}
                    onChange={e => {
                      setNotProvider(e.target.checked);
                      runValidFiled("cmc_Email");
                    }}
                    checked={notProvider || isFinCen}
                  >
                    <b>Not Provided</b>
                  </Checkbox>
                ) : (
                  <Form.Item name="isEmailConfirmed" valuePropName="checked" noStyle>
                    <Checkbox
                      style={{ marginTop: 8 }}
                      disabled={isFinCen}
                      onChange={() => runValidFiled("cmc_Email")}
                    >
                      <b>Email Confirmed</b>
                    </Checkbox>
                  </Form.Item>
                )}
              </div>
              <div className="member-row__phone-group">
              <Form.Item<MemberInfo>
                name={"cmc_Telephone"}
                label="Phone Number"
                rules={[
                  {
                    required: !isFinCen,
                    message: "Please input your phone!",
                    min: 12,
                  },
                ]}
              >
                <Input
                  data-testid={`mat-input-phone-${props.MemberInfo.key}`}
                  disabled={isFinCen}
                  placeholder="Phone Number"
                  onChange={event => {
                    const { value } = event.target;
                    const formattedValue = getPhoneFormat(value);
                    form.setFieldsValue({
                      cmc_Telephone: formattedValue,
                    });
                  }}
                />
              </Form.Item>
              <Form.Item<MemberInfo>
                name="cmc_Type"
                label="Phone Type"
                rules={[
                  {
                    required: !isFinCen,
                    message: "Please select Phone Type!",
                  },
                ]}
              >
                <Select
                  data-testid={`mat-select-phone-type-${props.MemberInfo.key}`}
                  className="select-short"
                  disabled={isFinCen}
                  placeholder="Type..."
                  options={[
                    { value: "Mobile", label: "Mobile" },
                    { value: "Home", label: "Home" },
                    { value: "Work", label: "Work" },
                  ]}
                />
              </Form.Item>
              </div>
            </div>

            <div className="member-row__grid">
              <Form.Item<MemberInfo>
                label="Term Length"
                name="termLength"
              >
                <Select
                  data-testid={`mat-select-term-length-${props.MemberInfo.key}`}
                  className="select-short"
                  disabled={isFinCen}
                  placeholder="Length..."
                  options={[
                    { value: 1, label: "1 Year" },
                    { value: 2, label: "2 Years" },
                    { value: 3, label: "3 Years" },
                  ]}
                />
              </Form.Item>
              <div ref={beginTermRef}>
              <Form.Item<MemberInfo>
                label="Begin Term"
                name="termStartStrPicker"
                rules={[{ required: !isFinCen, message: "Please input Begin Term!" }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  onChange={date => { form.setFieldsValue({ termStartStr: getDataStr(date) }); }}
                  disabled={isFinCen}
                  format={"MM/DD/YYYY"}
                />
              </Form.Item>
              </div>
              <Form.Item<MemberInfo> name="termStartStr" hidden>
                <Input />
              </Form.Item>
              <div ref={endTermRef}>
              <Form.Item<MemberInfo>
                label="End Term"
                name="termEndStrPicker"
                rules={[{ required: !isFinCen, message: "Please input End Term!" }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  onChange={date => { form.setFieldsValue({ termEndStr: getDataStr(date) }); }}
                  disabled={isFinCen}
                  format={"MM/DD/YYYY"}
                />
              </Form.Item>
              </div>
              <Form.Item<MemberInfo> name="termEndStr" hidden>
                <Input />
              </Form.Item>
              {applicationStore.displaySendFinancials ? (
                <Form.Item<MemberInfo>
                  label="Send Financials"
                  name="cm_SendFinancial"
                  valuePropName="checked"
                >
                  <Checkbox />
                </Form.Item>
              ) : null}
            </div>

          </Col>

          {/* Buttons */}
          <Col
            span={2}
            style={{
              display: "flex",
              justifyContent: "space-between",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            {!isFinCen && (
              <Popconfirm
                title="Are you sure?"
                description="Are you sure you want to delete this member record?"
                onConfirm={() => {
                  props.removeForm(props.MemberInfo.key);
                }}
                okText="Delete"
                cancelText="Cancel"
              >
                <Button
                  data-testid={`mat-btn-delete-member-${props.MemberInfo.key}`}
                  danger
                  type="primary"
                  shape="circle"
                  icon={<CloseOutlined />}
                  size="large"
                />
              </Popconfirm>
            )}
            {isEditForm ? (
              <Button data-testid={`mat-btn-save-member-${props.MemberInfo.key}`} size="large" type="primary" htmlType="submit">
                Save
              </Button>
            ) : null}
          </Col>
        </Row>
      </Form>
    </Card>
  );
});